import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Login.css';

const Login = () => {
    const [resetToken, setResetToken] = useState('');
    const [mode, setMode] = useState('login');
    const [resetStatus, setResetStatus] = useState('checking');
    const [validationAttempt, setValidationAttempt] = useState(0);
    const [username, setUsername] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    // Segundo passo: desafio do /login, método em uso e o método da app
    // ('autenticador' ou 'configurar') para onde se volta a partir do email.
    const [desafio, setDesafio] = useState('');
    const [metodo, setMetodo] = useState('autenticador');
    const [metodoApp, setMetodoApp] = useState('autenticador');
    const [emailMascarado, setEmailMascarado] = useState(null);
    const [configuracao, setConfiguracao] = useState(null);
    const [codigo, setCodigo] = useState('');
    const { login, iniciarSessao, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fragment = new URLSearchParams(location.hash.slice(1));
        if (!fragment.has('repor')) return;
        setResetToken(fragment.get('repor') || '');
        setMode('reset');
        setResetStatus('checking');
        setPassword('');
        setConfirmation('');
        setMessage('');
        setValidationAttempt(value => value + 1);
        // Mantém o segredo apenas em memória e acompanha links abertos nesta mesma página.
        navigate(location.pathname + location.search, { replace: true });
    }, [location.hash, location.pathname, location.search, navigate]);

    useEffect(() => {
        if (mode !== 'reset') return;
        let cancelled = false;
        setResetStatus('checking');
        setError('');
        api.post('/auth/password-reset/validate', { token: resetToken })
            .then(() => { if (!cancelled) setResetStatus('valid'); })
            .catch(err => {
                if (cancelled) return;
                setResetStatus(err.response?.status === 400 ? 'invalid' : 'error');
                setError(err.response?.data?.message || 'Não foi possível verificar a ligação. Tente novamente.');
            });
        return () => { cancelled = true; };
    }, [mode, resetToken, validationAttempt]);

    const changeMode = next => {
        setMode(next);
        setError('');
        setMessage('');
        setPassword('');
        setConfirmation('');
        setShowPassword(false);
        setDesafio('');
        setConfiguracao(null);
        setCodigo('');
        if (next === 'forgot') setIdentifier(username);
    };

    // Erro do segundo passo; se o desafio expirou, volta a pedir a palavra-passe.
    const erroDoisPassos = err => {
        const msg = err.response?.data?.message || 'Não foi possível concluir o pedido. Tente novamente.';
        if (err.response?.data?.desafio_expirado) changeMode('login');
        setError(msg);
    };

    const carregarConfiguracao = async valor => {
        try {
            const response = await api.post('/auth/2fa/configurar', { desafio: valor });
            setConfiguracao({ qr: response.data.qr, segredo: response.data.segredo });
        } catch (err) {
            erroDoisPassos(err);
        }
    };

    const iniciarDoisPassos = ({ desafio: valor, metodo: inicial, email }) => {
        setMode('2fa');
        setPassword('');
        setShowPassword(false);
        setCodigo('');
        setDesafio(valor);
        setMetodo(inicial);
        setMetodoApp(inicial);
        setEmailMascarado(email);
        setConfiguracao(null);
        if (inicial === 'configurar') carregarConfiguracao(valor);
    };

    const pedirCodigoEmail = async () => {
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const response = await api.post('/auth/2fa/email', { desafio }, { timeout: 45000 });
            setMetodo('email');
            setCodigo('');
            setMessage(response.data.message);
        } catch (err) {
            erroDoisPassos(err);
        } finally {
            setLoading(false);
        }
    };

    const voltarParaApp = () => {
        setError('');
        setMessage('');
        setCodigo('');
        setMetodo(metodoApp);
        if (metodoApp === 'configurar' && !configuracao) carregarConfiguracao(desafio);
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');
        setMessage('');
        if (mode === 'reset' && password !== confirmation) {
            setError('As palavras-passe não coincidem.');
            return;
        }
        setLoading(true);
        try {
            if (mode === 'forgot') {
                const response = await api.post('/auth/forgot-password', { identifier: identifier.trim() }, { timeout: 45000 });
                setMessage(response.data.message);
            } else if (mode === 'reset') {
                const response = await api.post('/auth/reset-password', { token: resetToken, password });
                logout();
                setResetToken('');
                changeMode('login');
                setMessage(response.data.message);
            } else if (mode === '2fa') {
                const response = await api.post('/auth/2fa/verificar', {
                    desafio, codigo, metodo: metodo === 'email' ? 'email' : 'autenticador'
                });
                iniciarSessao(response.data.token, response.data.user);
                navigate(response.data.user?.perfil_mobile ? '/mobile-dashboard' : '/');
            } else {
                const result = await login(username, password);
                if (result.success) {
                    navigate(result.user?.perfil_mobile ? '/mobile-dashboard' : '/');
                } else if (result.doisPassos) {
                    iniciarDoisPassos(result.doisPassos);
                } else {
                    setError(result.message || 'Falha no login');
                }
            }
        } catch (err) {
            if (mode === '2fa') {
                erroDoisPassos(err);
                setCodigo('');
                return;
            }
            setError(err.response?.data?.message || 'Não foi possível concluir o pedido. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const passwordInput = (id, label, repeat = false) => (
        <div className="form-group">
            <label htmlFor={id}>{label}</label>
            <div className="password-wrapper">
                <input
                    type={showPassword ? 'text' : 'password'}
                    id={id}
                    value={repeat ? confirmation : password}
                    onChange={e => repeat ? setConfirmation(e.target.value) : setPassword(e.target.value)}
                    autoComplete={mode === 'reset' ? 'new-password' : 'current-password'}
                    minLength={mode === 'reset' ? 8 : undefined}
                    maxLength={mode === 'reset' ? 72 : undefined}
                    required
                    disabled={loading}
                />
                {!repeat && <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
                    aria-pressed={showPassword}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                        {showPassword && <path d="M2 2l20 20" />}
                    </svg>
                </button>}
            </div>
        </div>
    );

    return (
        <div className="login-container">
            <div className="login-shell">
                <div className="login-brand"><img src="/assets/logo-white.png" alt="SPROD" className="login-logo" /></div>
                <div className="login-box">
                    <div className="login-header">
                        <h1>{mode === 'forgot' ? 'Recuperar palavra-passe' : mode === 'reset' ? 'Nova palavra-passe'
                            : mode === '2fa' ? (metodo === 'configurar' ? 'Configurar a app autenticadora' : metodo === 'email' ? 'Código por email' : 'Verificação em dois passos')
                            : 'Entrar no sistema'}</h1>
                        {mode === 'forgot' && <p>Indique o nome de utilizador ou o email associado à sua conta.</p>}
                        {mode === 'reset' && resetStatus === 'valid' && <p>Escolha uma palavra-passe com pelo menos 8 caracteres.</p>}
                        {mode === '2fa' && metodo === 'configurar' && <p>Leia o QR com uma app autenticadora (Google Authenticator, Microsoft Authenticator ou outra) e introduza o código de 6 dígitos que aparece. Só é preciso fazer isto uma vez.</p>}
                        {mode === '2fa' && metodo === 'autenticador' && <p>Introduza o código de 6 dígitos que aparece na app autenticadora.</p>}
                    </div>
                    {error && <div className="error-message" role="alert">{error}</div>}
                    {message && <div className="login-success" role="status">{message}</div>}
                    {mode === 'reset' && resetStatus === 'checking' && <p role="status">A verificar a ligação…</p>}
                    {mode === 'reset' && resetStatus === 'error' && <button type="button" className="login-button" onClick={() => setValidationAttempt(value => value + 1)}>Tentar novamente</button>}
                    {(mode !== 'reset' || resetStatus === 'valid') && <form onSubmit={handleSubmit} className="login-form">
                        {mode === 'forgot' ? (
                            <div className="form-group">
                                <label htmlFor="identifier">Nome de utilizador ou email</label>
                                <input id="identifier" type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                                    value={identifier} maxLength={254} onChange={e => setIdentifier(e.target.value)} required disabled={loading} />
                            </div>
                        ) : mode === '2fa' ? (
                            <>
                                {metodo === 'configurar' && (configuracao ? (
                                    <div className="login-qr">
                                        <img src={configuracao.qr} alt="QR para adicionar a conta SPROD à app autenticadora" width="200" height="200" />
                                        <details>
                                            <summary>Não consegue ler o QR?</summary>
                                            <p>Na app, escolha introduzir uma chave e use:</p>
                                            <code className="login-secret">{configuracao.segredo.match(/.{1,4}/g).join(' ')}</code>
                                        </details>
                                    </div>
                                ) : !error && <p role="status" className="login-qr-loading">A preparar o QR…</p>)}
                                <div className="form-group">
                                    <label htmlFor="codigo">{metodo === 'email' ? 'Código recebido por email' : 'Código da app'}</label>
                                    <input id="codigo" className="login-code-input" type="text" inputMode="numeric" autoComplete="one-time-code"
                                        pattern="[0-9]{6}" maxLength={6} placeholder="000000" autoFocus
                                        value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required disabled={loading || (metodo === 'configurar' && !configuracao)} />
                                </div>
                            </>
                        ) : mode === 'login' ? (
                            <>
                                <div className="form-group">
                                    <label htmlFor="username">Nome de utilizador</label>
                                    <input type="text" id="username" value={username} onChange={e => setUsername(e.target.value)}
                                        autoCapitalize="none" autoCorrect="off" autoComplete="username" spellCheck={false} required disabled={loading} />
                                </div>
                                {passwordInput('password', 'Palavra-passe')}
                            </>
                        ) : (
                            <>
                                {passwordInput('new-password', 'Nova palavra-passe')}
                                {passwordInput('confirm-password', 'Confirmar palavra-passe', true)}
                            </>
                        )}
                        <button type="submit" disabled={loading || (mode === '2fa' && codigo.length !== 6)} className="login-button">
                            {loading ? 'A processar…' : mode === 'forgot' ? 'Enviar ligação de recuperação' : mode === 'reset' ? 'Guardar palavra-passe' : mode === '2fa' ? 'Verificar e entrar' : 'Entrar'}
                        </button>
                    </form>}
                    <div className="login-footer">
                        {mode === 'login' ? <button type="button" className="login-link" disabled={loading} onClick={() => changeMode('forgot')}>Esqueceu-se da palavra-passe?</button> : mode === '2fa' ? (
                            <>
                                {metodo === 'email' ? (
                                    <>
                                        <button type="button" className="login-link" disabled={loading} onClick={pedirCodigoEmail}>Reenviar código</button>
                                        <button type="button" className="login-link" disabled={loading} onClick={voltarParaApp}>Usar a app autenticadora</button>
                                    </>
                                ) : emailMascarado ? (
                                    <button type="button" className="login-link" disabled={loading} onClick={pedirCodigoEmail}>A app não funciona? Enviar código para {emailMascarado}</button>
                                ) : (
                                    <p>Sem acesso à app autenticadora? Peça ao administrador para repor a verificação em dois passos.</p>
                                )}
                                <button type="button" className="login-link" disabled={loading} onClick={() => changeMode('login')}>Voltar ao login</button>
                            </>
                        ) : (
                            <>
                                {mode === 'reset' && <button type="button" className="login-link" disabled={loading} onClick={() => changeMode('forgot')}>Pedir uma nova ligação</button>}
                                <button type="button" className="login-link" disabled={loading} onClick={() => changeMode('login')}>Voltar ao login</button>
                            </>
                        )}
                        {mode === 'forgot' && <p>A recuperação está disponível para contas com email válido. Se não tiver email associado, contacte o administrador. Se partilha o email com outra conta, use o nome de utilizador.</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
